import { useMemo } from 'react';
import { OutgoingEventProductDto, OutgoingSectionDto } from 'api/dtos/events/products';

interface UseSectionHelpersProps {
  tiers?: OutgoingEventProductDto[];
  sections?: OutgoingSectionDto[];
}

export const useSectionHelpers = ({ 
  tiers, 
  sections 
}: UseSectionHelpersProps) => {
  const tiersBySection = useMemo(() => {
    if (!tiers) return {};
    
    return tiers.reduce((acc, tier) => {
      const sectionId = tier.sectionId || 'default';
      acc[sectionId] = [...(acc[sectionId] || []), tier];
      return acc;
    }, {} as Record<string, OutgoingEventProductDto[]>);
  }, [tiers]);

  const sortedSections = useMemo(() => {
    if (!sections) return undefined;
    return [...sections].sort((a, b) => a.sortIndex - b.sortIndex);
  }, [sections]);

  const hasCustomSections = useMemo(() => {
    return sortedSections && sortedSections.length > 0;
  }, [sortedSections]);

  const totalTierCount = useMemo(() => {
    return tiers?.length || 0;
  }, [tiers]);

  const defaultTierCount = useMemo(() => {
    return tiersBySection.default?.length || 0;
  }, [tiersBySection]);

  const sectionStats = useMemo(() => {
    if (!sortedSections) return {};
    
    return sortedSections.reduce((acc, section) => {
      acc[section.id] = {
        tierCount: tiersBySection[section.id]?.length || 0,
        isEmpty: !tiersBySection[section.id]?.length
      };
      return acc;
    }, {} as Record<string, { tierCount: number; isEmpty: boolean }>);
  }, [sortedSections, tiersBySection]);

  return {
    tiersBySection,
    sortedSections,
    hasCustomSections,
    totalTierCount,
    defaultTierCount,
    sectionStats
  };
}; 